import { useCallback } from "react";
import { api } from "../utils/apiClient";
import { appMessage as message } from "../utils/appMessage";
import { persistConsent, sameConsent } from "./cookieConsentStorage";
import { wasNotified } from "../utils/notifiedError";

// Consent mutation callbacks for CookieConsentProvider.
export function useConsentActions({
  expectedVersion,
  isAuthenticated,
  currentUserId,
  consent,
  consentRef,
  lastSyncedRef,
  setConsent,
  setBannerVisible,
  setSettingsDraft,
  setSettingsModalOpen,
}) {
  const saveLocalConsent = useCallback(
    (next, source = "banner") => {
      const normalized = {
        first_party: !!next.first_party,
        third_party: !!next.third_party,
        consent_version: expectedVersion,
        source,
        updated_at: new Date().toISOString(),
      };
      setConsent(normalized);
      persistConsent(normalized);
      setBannerVisible(false);
      return normalized;
    },
    [expectedVersion, setBannerVisible, setConsent],
  );

  const persistServerConsent = useCallback(
    async (value) => {
      if (!isAuthenticated || !currentUserId) return;

      await api.post("/api/cookie-consent/save", {
        first_party: value.first_party,
        third_party: value.third_party,
        consent_version: value.consent_version,
        source: value.source || "banner",
      });
      // Mark as synced so the sync effect does not POST the same consent again.
      lastSyncedRef.current = `${currentUserId}:${value.updated_at}:${value.consent_version}`;
    },
    [currentUserId, isAuthenticated, lastSyncedRef],
  );

  const applyConsent = useCallback(
    async (next, source = "banner") => {
      const updated = saveLocalConsent(next, source);
      const changed = !sameConsent(consentRef.current, updated);
      let synced = true;

      try {
        await persistServerConsent(updated);
      } catch (err) {
        synced = false;
        if (!wasNotified(err)) message.error(err?.response?.data?.error || "Failed to sync cookie consent");
      }

      return { updated, changed, synced };
    },
    [consentRef, persistServerConsent, saveLocalConsent],
  );

  const acceptAll = useCallback(async () => {
    return applyConsent({ first_party: true, third_party: true }, "accept_all");
  }, [applyConsent]);

  const rejectAll = useCallback(async () => {
    return applyConsent({ first_party: false, third_party: false }, "reject_all");
  }, [applyConsent]);

  const openSettings = useCallback(() => {
    setSettingsDraft({
      first_party: consent?.first_party ?? true,
      third_party: consent?.third_party ?? true,
    });
    setSettingsModalOpen(true);
  }, [consent, setSettingsDraft, setSettingsModalOpen]);

  const closeSettings = useCallback(() => setSettingsModalOpen(false), [setSettingsModalOpen]);

  const saveConfigured = useCallback(
    async (next) => {
      const result = await applyConsent(next, "configure");
      setSettingsModalOpen(false);
      return result;
    },
    [applyConsent, setSettingsModalOpen],
  );

  return {
    persistServerConsent,
    acceptAll,
    rejectAll,
    openSettings,
    closeSettings,
    saveConfigured,
  };
}
